import React, { useContext, useState } from 'react';
import { Button, Input, Modal, notification } from 'antd';

import { SyncConflict } from '@totem/components/administration/deviceSyncMerges/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DEVICE_SYNC_CONFLICTS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  conflict: SyncConflict;
  onClose: (updated: boolean) => void;
};

const DeviceSyncDismissModal = ({ conflict, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [notes, setNotes] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleDismiss = () => {
    setIsSaving(true);

    fetch(`${DEVICE_SYNC_CONFLICTS_ENDPOINT}/${conflict.id}/dismiss`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ notes }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Dismiss failed (${res.status})`);
        }
        onClose(true);
      })
      .catch((err: Error) => {
        notification.error({
          message: 'Unable to dismiss the conflict',
          description: err.message,
        });
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal
      open
      title={<ModalTitle>Dismiss Conflict</ModalTitle>}
      onCancel={() => onClose(false)}
      footer={
        <ModalFooter>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="primary" onClick={handleDismiss} loading={isSaving}>
            Dismiss
          </Button>
        </ModalFooter>
      }
    >
      <p>
        Dismiss when these are genuinely different devices. The conflict stays
        dismissed if the sync finds the same devices again; the sync keeps
        skipping them.
      </p>
      <ul>
        {(conflict.devices ?? []).map((dev) => (
          <li key={dev.id}>{dev.displayName || dev.id}</li>
        ))}
      </ul>
      <Input.TextArea
        rows={3}
        placeholder="Why these are different devices"
        value={notes}
        onChange={(evt) => setNotes(evt.target.value)}
      />
    </Modal>
  );
};

export default DeviceSyncDismissModal;
